import { useEffect, useMemo, useRef, useState } from 'react';
import { useCharacterProgressionStore } from '../../store/characterProgressionSlice';
import { TALENT_TREE } from '../../data/talent-tree.config';
import { canSpend, spendPoint, respec } from '../../services/TalentTreeService';
import { closeInterface } from '../../services/InterfaceService';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { KeyDisplay } from './KeyDisplay';
import type { TalentCategory, TalentNodeId } from '../../types/talent.types';
import './TalentTreeScreen.scss';

const CATEGORIES: TalentCategory[] = ['offensive', 'defensive', 'utility'];

const PIN_ASSET = '/sprites/ui/talent-tree/pin.png';
const CATEGORY_LABEL: Record<TalentCategory, string> = {
  offensive: 'Offensif',
  defensive: 'Défensif',
  utility: 'Utilitaire',
};

interface PlacedNode {
  id: TalentNodeId;
  /** 0..1, left to right. */
  x: number;
  /** 0..1, measured from the bottom. */
  y: number;
}

interface Edge {
  a: TalentNodeId;
  b: TalentNodeId;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface CatLayout {
  category: TalentCategory;
  nodes: PlacedNode[];
  edges: Edge[];
}

// Layout derived purely from the `connectsTo` graph: per category, BFS from the
// root assigns each node a depth = a row (root at the bottom, climbing up).
// Within a row, the N nodes are spread evenly across the width — so everything
// stays visible whatever the count. Nodes with no `connectsTo` are dropped.
function computeCategoryLayout(category: TalentCategory): CatLayout {
  const all = Object.values(TALENT_TREE.nodes);
  const inCat = all.filter(
    (node) => node.category === category && node.connectsTo.length > 0,
  );
  const set = new Set(inCat.map((node) => node.id));
  const rootId = `talent-${category}-root`;

  const depth = new Map<TalentNodeId, number>();
  if (set.has(rootId)) {
    depth.set(rootId, 0);
    const queue = [rootId];
    while (queue.length > 0) {
      const id = queue.shift()!;
      const d = depth.get(id)!;
      for (const nb of TALENT_TREE.nodes[id].connectsTo) {
        if (set.has(nb) && !depth.has(nb)) {
          depth.set(nb, d + 1);
          queue.push(nb);
        }
      }
    }
  }
  // Nodes connected but unreachable from the root land on an extra top row.
  let maxDepth = 0;
  depth.forEach((d) => (maxDepth = Math.max(maxDepth, d)));
  for (const id of set) {
    if (!depth.has(id)) {
      depth.set(id, maxDepth + 1);
    }
  }
  maxDepth = 0;
  depth.forEach((d) => (maxDepth = Math.max(maxDepth, d)));

  const layers: TalentNodeId[][] = Array.from(
    { length: maxDepth + 1 },
    () => [],
  );
  depth.forEach((d, id) => layers[d].push(id));
  const layerCount = layers.length;

  const pos = new Map<TalentNodeId, PlacedNode>();
  layers.forEach((layer, d) => {
    if (d === 0) {
      layer.sort();
    } else {
      // Order each row by the average x of its parents in the row below, to
      // keep edges roughly untangled.
      const barycenter = (id: TalentNodeId): number => {
        const xs = TALENT_TREE.nodes[id].connectsTo
          .filter((nb) => depth.get(nb) === d - 1 && pos.has(nb))
          .map((nb) => pos.get(nb)!.x);
        return xs.length ? xs.reduce((s, v) => s + v, 0) / xs.length : 0.5;
      };
      layer.sort((a, b) => barycenter(a) - barycenter(b) || (a < b ? -1 : 1));
    }
    const count = layer.length;
    layer.forEach((id, k) => {
      pos.set(id, {
        id,
        x: (k + 1) / (count + 1),
        y: (d + 1) / (layerCount + 1),
      });
    });
  });

  const edges: Edge[] = [];
  for (const node of inCat) {
    for (const nb of node.connectsTo) {
      if (set.has(nb) && node.id < nb) {
        const a = pos.get(node.id)!;
        const b = pos.get(nb)!;
        edges.push({ a: node.id, b: nb, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
    }
  }

  return { category, nodes: Array.from(pos.values()), edges };
}

export const TalentTreeScreen = () => {
  const level = useCharacterProgressionStore((s) => s.level);
  const talentPoints = useCharacterProgressionStore((s) => s.talentPoints);
  const pointsSpent = useCharacterProgressionStore((s) => s.pointsSpent);

  const spentSet = useMemo(() => new Set(pointsSpent), [pointsSpent]);
  const layouts = useMemo(() => CATEGORIES.map(computeCategoryLayout), []);

  // Measure a column area in pixels and place both nodes AND edges against the
  // same box, so they share one coordinate system (all columns are the same
  // size, so one measurement applies to all).
  const areaRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = areaRef.current;
    if (!el) {
      return;
    }
    const measure = () => setArea({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [confirmingRespec, setConfirmingRespec] = useState(false);

  const { containerRef, focusedId } = useMenuNavigation({
    frozen: confirmingRespec,
    onConfirm: (id) => {
      if (confirmingRespec) {
        respec();
        setConfirmingRespec(false);
        return;
      }
      if (id.startsWith('node:')) {
        spendPoint(id.slice('node:'.length));
      }
    },
    onCancel: () => {
      if (confirmingRespec) {
        setConfirmingRespec(false);
      } else {
        closeInterface();
      }
    },
    onQuaternary: () => {
      if (!confirmingRespec && pointsSpent.length > 0) {
        setConfirmingRespec(true);
      }
    },
  });

  const focusedNode =
    focusedId?.startsWith('node:') &&
    TALENT_TREE.nodes[focusedId.slice('node:'.length)];

  return (
    <div className="talent-tree-overlay">
      <div className="talent-tree-header">
        <h1 className="talent-tree-title">Mur de Confiance</h1>
        <div className="talent-tree-stats">
          <span>Niveau {level}</span>
          <span className="stats-sep">·</span>
          <span>
            {talentPoints} confiance{talentPoints > 1 ? 's' : ''} dispo
          </span>
          <span className="stats-sep">·</span>
          <span>
            {pointsSpent.length} dépensée{pointsSpent.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="talent-panel" ref={containerRef}>
        {layouts.map((cl, ci) => (
          <div className="talent-col" key={cl.category}>
            <div
              className={`talent-col-title talent-col-title--${cl.category}`}
            >
              {CATEGORY_LABEL[cl.category]}
            </div>
            <div
              className="talent-col-area"
              ref={ci === 0 ? areaRef : undefined}
            >
              {/* Inactive links: thin grey lines. */}
              <svg className="talent-edges" width={area.w} height={area.h}>
                {cl.edges.map((e) =>
                  spentSet.has(e.a) && spentSet.has(e.b) ? null : (
                    <line
                      key={`${e.a}-${e.b}`}
                      x1={e.x1 * area.w}
                      y1={(1 - e.y1) * area.h}
                      x2={e.x2 * area.w}
                      y2={(1 - e.y2) * area.h}
                      className="talent-edge"
                    />
                  ),
                )}
              </svg>

              {/* Active links (both ends pinned): rope sprite. */}
              {cl.edges.map((e) => {
                if (!(spentSet.has(e.a) && spentSet.has(e.b))) {
                  return null;
                }
                const x1 = e.x1 * area.w;
                const y1 = (1 - e.y1) * area.h;
                const dx = e.x2 * area.w - x1;
                const dy = (1 - e.y2) * area.h - y1;
                return (
                  <div
                    key={`rope-${e.a}-${e.b}`}
                    className="talent-rope"
                    style={{
                      left: x1,
                      top: y1,
                      width: Math.hypot(dx, dy),
                      transform: `rotate(${(Math.atan2(dy, dx) * 180) / Math.PI}deg)`,
                    }}
                  />
                );
              })}

              {cl.nodes.map((nd) => {
                const node = TALENT_TREE.nodes[nd.id];
                const isSpent = spentSet.has(nd.id);
                const available = !isSpent && canSpend(nd.id);
                const state = isSpent
                  ? 'spent'
                  : available
                    ? 'available'
                    : 'locked';
                return (
                  <div
                    key={nd.id}
                    className={[
                      'talent-node',
                      `talent-node--${cl.category}`,
                      `talent-node--${state}`,
                      node.isNotable ? 'talent-node--notable' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{
                      left: nd.x * area.w,
                      top: (1 - nd.y) * area.h,
                    }}
                    data-nav-row={Math.round((1 - nd.y) * 100)}
                    data-nav-col={ci * 1000 + Math.round(nd.x * 100)}
                    data-nav-id={`node:${nd.id}`}
                    title={node.description ?? node.label ?? nd.id}
                  >
                    <div
                      className="talent-node-pin"
                      style={{ backgroundImage: `url(${PIN_ASSET})` }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="talent-tree-footer">
        <div className="talent-tree-focus">
          {focusedNode ? (
            <>
              <span className="focus-name">{focusedNode.label}</span>
              {focusedNode.description && (
                <span className="focus-desc">{focusedNode.description}</span>
              )}
            </>
          ) : (
            <span className="focus-empty">Choisis un talent.</span>
          )}
        </div>
        <div className="talent-tree-hint">
          <KeyDisplay context="interface" action="CONFIRM" size="sm" /> épingler
          · <KeyDisplay context="interface" action="QUATERNARY" size="sm" />{' '}
          tout décrocher ·{' '}
          <KeyDisplay context="interface" action="CANCEL" size="sm" /> retour
        </div>
      </div>

      {confirmingRespec && (
        <div className="talent-confirm">
          <div className="confirm-box">
            <div className="confirm-msg">
              Tout décrocher ? <strong>{pointsSpent.length}</strong> confiance
              {pointsSpent.length > 1 ? 's' : ''} rendue
              {pointsSpent.length > 1 ? 's' : ''}.
            </div>
            <div className="confirm-actions">
              <span>
                <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
                Valider
              </span>
              <span>
                <KeyDisplay context="interface" action="CANCEL" size="sm" />{' '}
                Annuler
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
